import React from "react";
import { Button, Popover, Tooltip } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  GlobalOutlined,
  MinusCircleOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import { map } from "lodash";
import { AICC_PROMPT_RESET_TARGET, AiccPromptResetTarget } from "constants/aiccPrompt";
import { AiccPromptDraftHealth } from "./draft";
import SaveBlockedPanel from "./SaveBlockedPanel";
import { promptVariableToken } from "../../PromptEditor/variables";
import {
  HEALTH_TITLE,
  RESET_ALREADY_DEFAULT,
  RESET_ALREADY_PUBLIC,
  RESET_BUTTON,
  RESET_PUBLIC_BUTTON,
  SAVE_BLOCKED_TITLE,
  SAVE_BUTTON,
  SAVE_NOTHING_CHANGED,
} from "../configs";
import styles from "./ValidationFooter.module.scss";

interface ResetState {
  isOffered: boolean;
  isRestorable: boolean;
}

interface Props {
  health: AiccPromptDraftHealth;
  resets: Record<AiccPromptResetTarget, ResetState>;
  isSaving: boolean;
  resettingTarget?: AiccPromptResetTarget;
  onSave: () => void;
  onReset: (target: AiccPromptResetTarget) => void;
}

function VariableHealth({ health }: Pick<Props, "health">) {
  return (
    <div className={styles.health}>
      <span className={styles.healthTitle}>{HEALTH_TITLE}</span>
      {map(health.variables, ({ name, isRequired, isPresent }) => (
        <span
          key={name}
          className={isPresent ? styles.healthPresent : isRequired ? styles.healthMissing : styles.healthAbsent}
        >
          {isPresent ? <CheckCircleFilled /> : isRequired ? <CloseCircleFilled /> : <MinusCircleOutlined />}
          {promptVariableToken(name)}
        </span>
      ))}
      {map(health.unknownVariables, (name) => (
        <span key={name} className={styles.healthMissing}>
          <CloseCircleFilled />
          {promptVariableToken(name)}
        </span>
      ))}
    </div>
  );
}

const RESET_ICON = {
  [AICC_PROMPT_RESET_TARGET.public]: <GlobalOutlined />,
  [AICC_PROMPT_RESET_TARGET.default]: <RollbackOutlined />,
};

const RESET_LABEL = {
  [AICC_PROMPT_RESET_TARGET.public]: RESET_PUBLIC_BUTTON,
  [AICC_PROMPT_RESET_TARGET.default]: RESET_BUTTON,
};

const RESET_ORDER = [AICC_PROMPT_RESET_TARGET.public, AICC_PROMPT_RESET_TARGET.default];

const RESET_ALREADY_THERE = {
  [AICC_PROMPT_RESET_TARGET.public]: RESET_ALREADY_PUBLIC,
  [AICC_PROMPT_RESET_TARGET.default]: RESET_ALREADY_DEFAULT,
};

interface ResetButtonProps {
  target: AiccPromptResetTarget;
  state: ResetState;
  isBusy: boolean;
  resettingTarget?: AiccPromptResetTarget;
  onReset: (target: AiccPromptResetTarget) => void;
}

function ResetButton({ target, state, isBusy, resettingTarget, onReset }: ResetButtonProps) {
  if (!state.isOffered) {
    return null;
  }

  const button = (
    <span>
      <Button
        shape="round"
        icon={RESET_ICON[target]}
        loading={resettingTarget === target}
        disabled={!state.isRestorable || isBusy}
        onClick={() => onReset(target)}
      >
        {RESET_LABEL[target]}
      </Button>
    </span>
  );

  return state.isRestorable ? button : <Tooltip title={RESET_ALREADY_THERE[target]}>{button}</Tooltip>;
}

function ValidationFooter({ health, resets, isSaving, resettingTarget, onSave, onReset }: Props) {
  const isBusy = isSaving || !!resettingTarget;
  const {
    canSave,
    blockedReason,
    isDirty,
    missingRequired,
    unknownVariables,
    malformedVariables,
    unsupported,
  } = health;
  const isBlockedByVariables =
    !!missingRequired.length || !!unknownVariables.length || !!malformedVariables.length || !!unsupported.length;
  const hint = blockedReason ?? (isDirty ? undefined : SAVE_NOTHING_CHANGED);

  const saveButton = (
    <span>
      <Button type="primary" shape="round" loading={isSaving} disabled={!canSave || isBusy} onClick={onSave}>
        {SAVE_BUTTON}
      </Button>
    </span>
  );

  return (
    <div className={styles.footer}>
      <VariableHealth health={health} />

      <div className={styles.footerActions}>
        {map(RESET_ORDER, (target) => (
          <ResetButton
            key={target}
            target={target}
            state={resets[target]}
            isBusy={isBusy}
            resettingTarget={resettingTarget}
            onReset={onReset}
          />
        ))}
        {isBlockedByVariables ? (
          <Popover
            title={SAVE_BLOCKED_TITLE}
            content={
              <SaveBlockedPanel
                missingRequired={missingRequired}
                unknownVariables={unknownVariables}
                malformedVariables={malformedVariables}
                unsupported={unsupported}
              />
            }
            placement="topRight"
          >
            {saveButton}
          </Popover>
        ) : (
          <Tooltip title={hint}>{saveButton}</Tooltip>
        )}
      </div>
    </div>
  );
}

export default ValidationFooter;
